import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '../../DataDomain';
import {
    IAllEntitlementsResponse,
    IAmazonAccountRecoveryRequest,
    IAmazonAccountRecoveryResponse,
    IAmazonActivationLinkRequest,
    IAmazonActivationLinkResponse,
    IAvailablePackagesResponse,
    IChannelListResponse,
    IEntitlementPurchaseResponse,
    IGetSubscriptionsTiersResponse,
    INetflixForgotAccountPayload,
    INetflixForgotAccountResponse,
    IPaymentSwitchGeneratePinRequest,
    IPaymentSwitchVerifyPinRequest,
    IPPVPurchaseGenerateOtpPayload,
    IPPVPurchaseGenerateOtpResponse,
    IPPVPurchasePinPayload,
    IPPVPurchasePinResponse,
    IPPVPurchaseVerifyOtpPayload,
    IPPVPurchaseVerifyOtpResponse,
    IProfileMiniEpgChannelsParams,
    IQitafGenerateOtpPayload,
    IQitafVerifyOtpPayload,
    ISubscribeEntitlementRequest,
    ITVODEntitlementsResponse,
    ITvodOtpGenerateRequest,
    ITvodOtpResponse,
    ITvodOtpVerifyRequest,
    ITvodPricingTemplateResponse,
    SubscriptionTiers,
    SVODEntitlement,
    TVODEntitlement,
    TVODPurchaseRequest,
} from '../../entity/interface/iEntitlements';
import { INetflixForgotAccountUseCase } from '../../core/usecases/entitlement/NetflixForgotAccountUseCase';
import { IQitafGenerateOtpUseCase } from '../../core/usecases/entitlement/QitafGenerateOtpUseCase';
import { IQitafTvodGenerateOtpUseCase } from '../../core/usecases/entitlement/QitafTvodGenerateOtpUseCase';
import { IQitafVerifyOtpUseCase } from '../../core/usecases/entitlement/QitafVerifyOtpUseCase';
import { IQitafTvodVerifyOtpUseCase } from '../../core/usecases/entitlement/QitafTvodVerifyOtpUseCase';
import { IAllowedCountriesUseCase } from '../../core/usecases/entitlement/AllowedCountriesUseCase';
import { IAvailablePackagesUseCase } from '../../core/usecases/entitlement/AvailablePackagesUseCase';
import { IGetTvodUseCase } from '../../core/usecases/entitlement/GetTVodsUseCase';
import { IAllowedChanelsUseCase } from '../../core/usecases/entitlement/AllowedChanelsUseCase';
import { IGetAllUseCase } from '../../core/usecases/entitlement/GetAllUseCase';
import { IGetProfileMiniEpgChannelsUseCase } from '../../core/usecases/entitlement/GetProfileMiniEpgChannelsUseCase';
import { ITvodPurchaseUseCase } from '../../core/usecases/entitlement/TvodPurchaseUseCase';
import { ISubscribeUseCase } from '../../core/usecases/entitlement/SubscribeUseCase';
import { IPaymentSwitchGeneratePinUseCase } from '../../core/usecases/entitlement/PaymentSwitchGeneratePinUseCase';
import { IGetAllSubscriptionTiersUseCase } from '../../core/usecases/entitlement/GetAllSubscriptionTiersUseCase';
import { IPaymentSwitchVerifyPinUseCase } from '../../core/usecases/entitlement/PaymentSwitchVerifyPinUseCase';
import { IGetTvodPricingTemplatesUseCase } from '../../core/usecases/entitlement/GetTvodPricingTemplatesUseCase';
import { IAmazonAccountRecoveryUseCase } from '../../core/usecases/entitlement/AmazonAccountRecoveryUseCase';
import { IGetAmazonActivationLinkUseCase } from '../../core/usecases/entitlement/GetAmazonActivationLinkUseCase';
import { ITvodOtpGenerateUseCase } from '../../core/usecases/entitlement/TvodOtpGenerateUseCase';
import { ITvodOtpVerifyUseCase } from '../../core/usecases/entitlement/TvodOtpVerifyUseCase';
import { IFormSignatureObjectUseCase } from '../../core/usecases/auth/FormSignatureObjectUseCase';
import { IPPVPurchaseGenerateOtpUseCase } from '../../core/usecases/entitlement/PVVPurchaseGenerateOtpUseCase';
import { IPPVPurchaseVerifyOtpUseCase } from '../../core/usecases/entitlement/PVVPurchaseVerifyOtpUseCase';
import { IPPVPurchasePinUseCase } from '../../core/usecases/entitlement/PVVPurchasePinUseCase';

export interface IEntitlementService {
    netflixForgotAccount(payload: INetflixForgotAccountPayload): Promise<INetflixForgotAccountResponse>;

    qitafGenerateOtp(
        subType: 'NEW' | 'EXISTING',
        language: string,
        payload: string | IQitafGenerateOtpPayload,
        token?: string | undefined,
        identityToken?: string | undefined
    ): Promise<string>;

    qitafTvodGenerateOtp(
        productId: string,
        qitafMsisdn: string,
        uid: string,
        apiKey: string,
        token: string,
        identityToken: string
    ): Promise<any>;

    qitafVerifyOtp(
        subType: 'NEW' | 'EXISTING',
        language: string,
        payload: string | IQitafVerifyOtpPayload,
        token?: string | undefined,
        identityToken?: string | undefined
    ): Promise<string>;

    qitafTvodVerifyOtp(
        productId: string,
        qitafMsisdn: string,
        qitafRefId: string,
        otp: number,
        amount: number,
        uid: string,
        apiKey: string,
        token: string,
        identityToken: string
    ): Promise<any>;

    allowedCountries(apiKey: string): Promise<string[]>;

    availablePackages(apiKey: string, username: string, token: string): Promise<IAvailablePackagesResponse['data']>;

    getTVods(apiKey: string, username: string, token: string): Promise<TVODEntitlement[]>;

    getAll(apiKey: string, username: string, token: string): Promise<IAllEntitlementsResponse['data']>;

    allowedChannels(
        apiKey: string,
        username: string,
        language: string,
        device: string,
        country: string,
        token: string,
        identityToken: string
    ): Promise<string[]>;

    getProfileMiniEpgChannels(
        apiKey: string,
        username: string,
        profile: string,
        query: IProfileMiniEpgChannelsParams,
        token: string
    ): Promise<string[]>;

    tVodPurchase(
        username: string,
        payload: TVODPurchaseRequest,
        token: string,
        identityToken: string
    ): Promise<IEntitlementPurchaseResponse>;

    paymentSwitchGeneratePin(
        payload: IPaymentSwitchGeneratePinRequest,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<boolean>;

    paymentSwitchVerifyPin(
        payload: IPaymentSwitchVerifyPinRequest,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<boolean>;

    getAllSubscriptionTiers(
        apiKey: string,
        country: string,
        device: string,
        productKey: string,
        identityToken: string,
        groupBy?: string,
        showAllSubscriptions?: string,
        apiVersion?: string
    ): Promise<IGetSubscriptionsTiersResponse['data']>;

    getAmazonActivationLink(payload: IAmazonActivationLinkRequest): Promise<IAmazonActivationLinkResponse>;

    amazonAccountRecovery(payload: IAmazonAccountRecoveryRequest): Promise<IAmazonAccountRecoveryResponse>;

    getTvodPricingTemplates(operator: string): Promise<ITvodPricingTemplateResponse>;

    tvodOtpGenerate(
        payload: ITvodOtpGenerateRequest,
        username: string,
        token: string,
        identityToken: string
    ): Promise<ITvodOtpResponse>;

    tvodOtpVerify(
        payload: ITvodOtpVerifyRequest,
        username: string,
        token: string,
        identityToken: string
    ): Promise<ITvodOtpResponse>;

    ppvPurchaseGenerateOtp(
        payload: IPPVPurchaseGenerateOtpPayload,
        username: string,
        authToken: string,
        authSign: string,
        identityToken: string,
        apiKey: string
    ): Promise<IPPVPurchaseGenerateOtpResponse>;

    ppvPurchaseVerifyOtp(
        payload: IPPVPurchaseVerifyOtpPayload,
        username: string,
        authToken: string,
        authSign: string,
        identityToken: string,
        apiKey: string
    ): Promise<IPPVPurchaseVerifyOtpResponse>;

    ppvPurchasePin(
        payload: IPPVPurchasePinPayload,
        username: string,
        authToken: string,
        authSign: string,
        identityToken: string,
        apiKey: string
    ): Promise<IPPVPurchasePinResponse>;
}

export class EntitlementService implements IEntitlementService {
    // Lazy getters for dependencies to avoid circular dependencies
    private get netflixForgotAccountUseCase(): INetflixForgotAccountUseCase {
        return container.resolve<INetflixForgotAccountUseCase>(DI.USECASES.INetflixForgotAccountUseCase);
    }

    private get qitafGenerateOtpUseCase(): IQitafGenerateOtpUseCase {
        return container.resolve<IQitafGenerateOtpUseCase>(DI.USECASES.IQitafGenerateOtpUseCase);
    }

    private get qitafTvodGenerateOtpUseCase(): IQitafTvodGenerateOtpUseCase {
        return container.resolve<IQitafTvodGenerateOtpUseCase>(DI.USECASES.IQitafTvodGenerateOtpUseCase);
    }

    private get qitafVerifyOtpUseCase(): IQitafVerifyOtpUseCase {
        return container.resolve<IQitafVerifyOtpUseCase>(DI.USECASES.IQitafVerifyOtpUseCase);
    }

    private get qitafTvodVerifyOtpUseCase(): IQitafTvodVerifyOtpUseCase {
        return container.resolve<IQitafTvodVerifyOtpUseCase>(DI.USECASES.IQitafTvodVerifyOtpUseCase);
    }

    private get allowedCountriesUseCase(): IAllowedCountriesUseCase {
        return container.resolve<IAllowedCountriesUseCase>(DI.USECASES.IAllowedCountriesUseCase);
    }

    private get availablePackagesUseCase(): IAvailablePackagesUseCase {
        return container.resolve<IAvailablePackagesUseCase>(DI.USECASES.IAvailablePackagesUseCase);
    }

    private get getTVodsUseCase(): IGetTvodUseCase {
        return container.resolve<IGetTvodUseCase>(DI.USECASES.IGetTvodUseCase);
    }

    private get getAllUseCase(): IGetAllUseCase {
        return container.resolve<IGetAllUseCase>(DI.USECASES.IGetAllUseCase);
    }

    private get allowedChannelsUseCase(): IAllowedChanelsUseCase {
        return container.resolve<IAllowedChanelsUseCase>(DI.USECASES.IAllowedChanelsUseCase);
    }

    private get getProfileMiniEpgChannelsUseCase(): IGetProfileMiniEpgChannelsUseCase {
        return container.resolve<IGetProfileMiniEpgChannelsUseCase>(DI.USECASES.IGetProfileMiniEpgChannelsUseCase);
    }

    private get subscribeUseCase(): ISubscribeUseCase {
        return container.resolve<ISubscribeUseCase>(DI.USECASES.ISubscribeUseCase);
    }

    private get tVodPurchaseUseCase(): ITvodPurchaseUseCase {
        return container.resolve<ITvodPurchaseUseCase>(DI.USECASES.ITvodPurchaseUseCase);
    }

    private get paymentSwitchGeneratePinUseCase(): IPaymentSwitchGeneratePinUseCase {
        return container.resolve<IPaymentSwitchGeneratePinUseCase>(DI.USECASES.IPaymentSwitchGeneratePinUseCase);
    }

    private get paymentSwitchVerifyPinUseCase(): IPaymentSwitchVerifyPinUseCase {
        return container.resolve<IPaymentSwitchVerifyPinUseCase>(DI.USECASES.IPaymentSwitchVerifyPinUseCase);
    }

    private get getAllSubscriptionTiersUseCase(): IGetAllSubscriptionTiersUseCase {
        return container.resolve<IGetAllSubscriptionTiersUseCase>(DI.USECASES.IGetAllSubscriptionTiersUseCase);
    }

    private get getAmazonActivationLinkUseCase(): IGetAmazonActivationLinkUseCase {
        return container.resolve<IGetAmazonActivationLinkUseCase>(DI.USECASES.IGetAmazonActivationLinkUseCase);
    }

    private get amazonAccountRecoveryUseCase(): IAmazonAccountRecoveryUseCase {
        return container.resolve<IAmazonAccountRecoveryUseCase>(DI.USECASES.IAmazonAccountRecoveryUseCase);
    }

    private get getTvodPricingTemplatesUseCase(): IGetTvodPricingTemplatesUseCase {
        return container.resolve<IGetTvodPricingTemplatesUseCase>(DI.USECASES.IGetTvodPricingTemplatesUseCase);
    }

    private get tvodOtpGenerateUseCase(): ITvodOtpGenerateUseCase {
        return container.resolve<ITvodOtpGenerateUseCase>(DI.USECASES.ITvodOtpGenerateUseCase);
    }

    private get tvodOtpVerifyUseCase(): ITvodOtpVerifyUseCase {
        return container.resolve<ITvodOtpVerifyUseCase>(DI.USECASES.ITvodOtpVerifyUseCase);
    }

    private get formSignatureObjectUseCase(): IFormSignatureObjectUseCase {
        return container.resolve<IFormSignatureObjectUseCase>(DI.USECASES.IFormSignatureObjectUseCase);
    }

    private get ppvPurchaseGenerateOtpUseCase(): IPPVPurchaseGenerateOtpUseCase {
        return container.resolve<IPPVPurchaseGenerateOtpUseCase>(DI.USECASES.IPPVPurchaseGenerateOtpUseCase);
    }

    private get ppvPurchaseVerifyOtpUseCase(): IPPVPurchaseVerifyOtpUseCase {
        return container.resolve<IPPVPurchaseVerifyOtpUseCase>(DI.USECASES.IPPVPurchaseVerifyOtpUseCase);
    }

    private get ppvPurchasePinUseCase(): IPPVPurchasePinUseCase {
        return container.resolve<IPPVPurchasePinUseCase>(DI.USECASES.IPPVPurchasePinUseCase);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    async netflixForgotAccount(payload: INetflixForgotAccountPayload): Promise<INetflixForgotAccountResponse> {
        const csrfToken = await this.formSignatureObjectUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }
        const response = await this.netflixForgotAccountUseCase.execute(payload, csrfToken);
        return response;
    }

    async qitafGenerateOtp(
        subType: 'NEW' | 'EXISTING',
        language: string,
        payload: string | IQitafGenerateOtpPayload,
        token?: string | undefined,
        identityToken?: string | undefined
    ): Promise<string> {
        const response = await this.qitafGenerateOtpUseCase.execute(subType, language, payload, token, identityToken);
        return response;
    }

    async qitafTvodGenerateOtp(
        productId: string,
        qitafMsisdn: string,
        uid: string,
        apiKey: string,
        token: string,
        identityToken: string
    ): Promise<any> {
        const response = await this.qitafTvodGenerateOtpUseCase.execute(
            productId,
            qitafMsisdn,
            uid,
            apiKey,
            token,
            identityToken
        );
        return response;
    }

    async qitafVerifyOtp(
        subType: 'NEW' | 'EXISTING',
        language: string,
        payload: string | IQitafVerifyOtpPayload,
        token?: string | undefined,
        identityToken?: string | undefined
    ): Promise<string> {
        const response = await this.qitafVerifyOtpUseCase.execute(subType, language, payload, token, identityToken);
        return response;
    }

    async qitafTvodVerifyOtp(
        productId: string,
        qitafMsisdn: string,
        qitafRefId: string,
        otp: number,
        amount: number,
        uid: string,
        apiKey: string,
        token: string,
        identityToken: string
    ): Promise<any> {
        const response = await this.qitafTvodVerifyOtpUseCase.execute(
            productId,
            qitafMsisdn,
            qitafRefId,
            otp,
            amount,
            uid,
            apiKey,
            token,
            identityToken
        );
        return response;
    }

    async allowedCountries(apiKey: string): Promise<any> {
        const response = await this.allowedCountriesUseCase.execute(apiKey);
        return response;
    }

    async availablePackages(
        apiKey: string,
        username: string,
        token: string
    ): Promise<IAvailablePackagesResponse['data']> {
        const response = await this.availablePackagesUseCase.execute(apiKey, username, token);
        return response;
    }

    async getTVods(apiKey: string, username: string, token: string): Promise<ITVODEntitlementsResponse | any> {
        const response = await this.getTVodsUseCase.execute(apiKey, username, token);
        return response;
    }

    async getAll(apiKey: string, username: string, token: string): Promise<IAllEntitlementsResponse['data']> {
        const response = await this.getAllUseCase.execute(apiKey, username, token);
        return response;
    }

    async allowedChannels(
        apiKey: string,
        username: string,
        language: string,
        device: string,
        country: string,
        token: string,
        identityToken: string
    ): Promise<IChannelListResponse | any> {
        const response = await this.allowedChannelsUseCase.execute(
            apiKey,
            username,
            language,
            device,
            country,
            token,
            identityToken
        );
        return response;
    }

    async getProfileMiniEpgChannels(
        apiKey: string,
        username: string,
        profile: string,
        query: IProfileMiniEpgChannelsParams,
        token: string
    ): Promise<IChannelListResponse | any> {
        const response = await this.getProfileMiniEpgChannelsUseCase.execute(apiKey, username, profile, query, token);
        return response;
    }

    async subscribe(
        username: string,
        payload: ISubscribeEntitlementRequest,
        token: string
    ): Promise<IEntitlementPurchaseResponse> {
        const response = await this.subscribeUseCase.execute(username, payload, token);
        return response;
    }

    async tVodPurchase(
        username: string,
        payload: TVODPurchaseRequest,
        token: string,
        identityToken: string
    ): Promise<IEntitlementPurchaseResponse> {
        const response = await this.tVodPurchaseUseCase.execute(username, payload, token, identityToken);
        return response;
    }

    async paymentSwitchGeneratePin(
        payload: IPaymentSwitchGeneratePinRequest,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<any> {
        const csrfToken = await this.formSignatureObjectUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }
        const response = await this.paymentSwitchGeneratePinUseCase.execute(
            payload,
            authToken,
            authSign,
            identityToken,
            csrfToken
        );
        return response;
    }

    async paymentSwitchVerifyPin(
        payload: IPaymentSwitchVerifyPinRequest,
        authToken: string,
        authSign: string,
        identityToken: string
    ): Promise<any> {
        const csrfToken = await this.formSignatureObjectUseCase.execute();
        if (!csrfToken) {
            throw new Error('Invalid API token');
        }
        const response = await this.paymentSwitchVerifyPinUseCase.execute(
            payload,
            authToken,
            authSign,
            identityToken,
            csrfToken
        );
        return response;
    }

    async getAllSubscriptionTiers(
        apiKey: string,
        country: string,
        device: string,
        productKey: string,
        identityToken: string,
        groupBy?: string,
        showAllSubscriptions?: string,
        apiVersion?: string
    ): Promise<IGetSubscriptionsTiersResponse['data']> {
        const response = await this.getAllSubscriptionTiersUseCase.execute(
            apiKey,
            country,
            device,
            productKey,
            identityToken,
            groupBy,
            showAllSubscriptions,
            apiVersion
        );
        return response;
    }

    async getAmazonActivationLink(payload: IAmazonActivationLinkRequest): Promise<IAmazonActivationLinkResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        const response = await this.getAmazonActivationLinkUseCase.execute(payload, apiToken);
        return response;
    }

    async amazonAccountRecovery(payload: IAmazonAccountRecoveryRequest): Promise<IAmazonAccountRecoveryResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        const response = await this.amazonAccountRecoveryUseCase.execute(payload, apiToken);
        return response;
    }

    async getTvodPricingTemplates(operator: string): Promise<ITvodPricingTemplateResponse> {
        const response = await this.getTvodPricingTemplatesUseCase.execute(operator);
        return response;
    }

    async tvodOtpGenerate(
        payload: any,
        username: string,
        token: string,
        identityToken: string
    ): Promise<ITvodOtpResponse> {
        const response = await this.tvodOtpGenerateUseCase.execute(payload, username, token, identityToken);
        return response;
    }

    async tvodOtpVerify(
        payload: ITvodOtpVerifyRequest,
        username: string,
        token: string,
        identityToken: string
    ): Promise<ITvodOtpResponse> {
        const response = await this.tvodOtpVerifyUseCase.execute(payload, username, token, identityToken);
        return response;
    }

    async ppvPurchaseGenerateOtp(
        payload: IPPVPurchaseGenerateOtpPayload,
        username: string,
        authToken: string,
        authSign: string,
        identityToken: string,
        apiKey: string
    ): Promise<IPPVPurchaseGenerateOtpResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        const response = await this.ppvPurchaseGenerateOtpUseCase.execute(
            payload,
            username,
            authToken,
            authSign,
            identityToken,
            apiToken,
            apiKey
        );
        return response;
    }

    async ppvPurchaseVerifyOtp(
        payload: IPPVPurchaseVerifyOtpPayload,
        username: string,
        authToken: string,
        authSign: string,
        identityToken: string,
        apiKey: string
    ): Promise<IPPVPurchaseVerifyOtpResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        const response = await this.ppvPurchaseVerifyOtpUseCase.execute(
            payload,
            username,
            authToken,
            authSign,
            identityToken,
            apiToken,
            apiKey
        );
        return response;
    }

    async ppvPurchasePin(
        payload: IPPVPurchasePinPayload,
        username: string,
        authToken: string,
        authSign: string,
        identityToken: string,
        apiKey: string
    ): Promise<IPPVPurchasePinResponse> {
        const apiToken = await this.formSignatureObjectUseCase.execute();
        if (!apiToken) {
            throw new Error('Invalid API token');
        }
        const response = await this.ppvPurchasePinUseCase.execute(
            payload,
            username,
            authToken,
            authSign,
            identityToken,
            apiToken,
            apiKey
        );
        return response;
    }
}
